// Who money is for (scopes) and where it came from (sources) used to be two
// hardcoded lists in this folder. They are the tenant's own data now: fetched
// once at the root of the app, read everywhere through this context.
//
// Everything downstream takes a plain string key. The label, the colour and the
// rollup behaviour come from here, and an unknown key — a scope deleted out
// from under an old row — degrades to the key itself rather than blowing up.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { apiGet } from "@/lib/api";
import { colorOf, PALETTE, type ColorName } from "@/lib/palette";

export type ScopeKind = "personal" | "business" | "excluded";

export const KIND_LABEL: Record<ScopeKind, string> = {
  personal: "Personal",
  business: "Business",
  excluded: "One-off",
};

// What choosing a kind actually does, said out loud in the editor — the whole
// point of a kind is the behaviour behind it.
export const KIND_HINT: Record<ScopeKind, string> = {
  personal: "Everyday spend. New entries default here.",
  business: "Billable or company spend. Gets a P&L line.",
  excluded: "One-off spend, left out of the burn baseline.",
};

export type ScopeDTO = {
  id: string;
  key: string;
  label: string;
  kind: ScopeKind;
  color: string;
  position: number;
  archived: boolean;
};

export type SourceDTO = {
  id: string;
  key: string;
  label: string;
  color: string;
  position: number;
  archived: boolean;
  defaultCurrency: string;
  feeBps: number;
  defaultScope: string;
  overdueDays: number | null;
};

export type TaxonomyDTO = {
  scopes: ScopeDTO[];
  sources: SourceDTO[];
  kinds: ScopeKind[];
  colors: string[];
};

const EMPTY: TaxonomyDTO = {
  scopes: [],
  sources: [],
  kinds: ["personal", "business", "excluded"],
  colors: Object.keys(PALETTE),
};

type Taxonomy = TaxonomyDTO & {
  /** Everything still in use — what the pickers offer. */
  activeScopes: ScopeDTO[];
  activeSources: SourceDTO[];
  scope: (key: string) => ScopeDTO | undefined;
  source: (key: string) => SourceDTO | undefined;
  scopeLabel: (key: string) => string;
  sourceLabel: (key: string) => string;
  scopeColor: (key: string) => (typeof PALETTE)[ColorName];
  sourceColor: (key: string) => (typeof PALETTE)[ColorName];
  /** Where a new entry starts: the first personal scope, else the first one. */
  defaultScope: string;
  excludedLabel: string;
  reload: () => void;
};

const TaxonomyContext = createContext<Taxonomy | null>(null);

export function TaxonomyProvider({
  children,
  fallback = null,
  enabled = true,
}: {
  children: ReactNode;
  /** Shown until the first load settles — see `settled` below. */
  fallback?: ReactNode;
  /** Off on the sign-in screen: there is no tenant to load one for yet. */
  enabled?: boolean;
}) {
  const [data, setData] = useState<TaxonomyDTO>(EMPTY);
  const [nonce, setNonce] = useState(0);
  // Nothing renders until the first load finishes either way. The entry forms
  // seed their state from the taxonomy on mount, so arriving late would leave a
  // form pointing at a scope that doesn't exist.
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    let active = true;
    apiGet<TaxonomyDTO>("/api/taxonomy")
      .then((next) => active && setData({ ...EMPTY, ...next }))
      // A failed load leaves the pickers empty rather than the app blank; the
      // screens that need a scope say so themselves.
      .catch(() => undefined)
      .finally(() => active && setSettled(true));
    return () => {
      active = false;
    };
  }, [enabled, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  const value = useMemo<Taxonomy>(() => {
    const scopeByKey = new Map(data.scopes.map((s) => [s.key, s]));
    const sourceByKey = new Map(data.sources.map((s) => [s.key, s]));
    const activeScopes = data.scopes.filter((s) => !s.archived);
    const activeSources = data.sources.filter((s) => !s.archived);
    const excluded = activeScopes.filter((s) => s.kind === "excluded");

    return {
      ...data,
      activeScopes,
      activeSources,
      scope: (key) => scopeByKey.get(key),
      source: (key) => sourceByKey.get(key),
      scopeLabel: (key) => scopeByKey.get(key)?.label ?? key,
      sourceLabel: (key) => sourceByKey.get(key)?.label ?? key,
      scopeColor: (key) => colorOf(scopeByKey.get(key)?.color),
      sourceColor: (key) => colorOf(sourceByKey.get(key)?.color),
      defaultScope:
        activeScopes.find((s) => s.kind === "personal")?.key ??
        activeScopes[0]?.key ??
        "",
      // The one-off bucket is named after the scope in it when there's exactly
      // one — "Trip" reads better than "One-off" when that's what it is.
      excludedLabel: excluded.length === 1 ? excluded[0].label : "One-off",
      reload,
    };
  }, [data, reload]);

  return (
    <TaxonomyContext value={value}>
      {enabled && !settled ? fallback : children}
    </TaxonomyContext>
  );
}

export function useTaxonomy(): Taxonomy {
  const value = useContext(TaxonomyContext);
  if (!value) throw new Error("useTaxonomy outside TaxonomyProvider");
  return value;
}

// What a source fills in when it's picked on the entry form: a default, never a
// lock. Unknown sources contribute nothing, which leaves the form as it was.
export function sourceDefaults(source: SourceDTO | undefined) {
  return {
    currency: source?.defaultCurrency ?? "MAD",
    feeBps: source?.feeBps ?? 0,
    scope: source?.defaultScope ?? "",
  };
}
